<script lang="ts" setup>
import { Tooltip, ShareDocumentModal } from "@/components";
import { useDocumentStore } from "@/store/document";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

const emit = defineEmits<{
  (e: "toggle-info"): void;
}>();

const documentStore = useDocumentStore();
const store = useStore();
const canUseSharedSpaces = computed(() =>
  store.getters.user.canUseSharedSpaces(),
);
const documentSpacePickerActive = ref<boolean>(false);

const openShareDialog = () => {
  if (!canUseSharedSpaces.value || !documentStore.isShareable) {
    return;
  }

  documentSpacePickerActive.value = true;
};

watch(canUseSharedSpaces, (enabled) => {
  if (!enabled) documentSpacePickerActive.value = false;
});
</script>

<template>
  <div class="title_container">
    <div class="right">
      <div class="share-dialog">
        <ShareDocumentModal
          v-if="canUseSharedSpaces && documentSpacePickerActive"
          :docId="documentStore.id ?? undefined"
          inherit-classic-theme
          @close="documentSpacePickerActive = false"
        />
      </div>
      <div
        v-if="canUseSharedSpaces"
        :class="{ disabled: !documentStore.isShareable }"
      >
        <tooltip class="bottom">
          <template v-slot>
            <span
              class="material-symbols-outlined icon"
              :class="{
                clickable: documentStore.isShareable,
                disabled: !documentStore.isShareable,
              }"
              @click="openShareDialog"
            >
              share
            </span>
          </template>
          <template #tooltip>
            <span v-if="documentStore.isShareable">
              {{ $t("navbar.shareDocument.label") }}
            </span>
            <span v-else>
              {{ $t("navbar.shareDocument.notShareable") }}
            </span>
          </template>
        </tooltip>
      </div>
      <tooltip class="bottom">
        <template v-slot>
          <span
            class="material-symbols-outlined icon clickable"
            @click="emit('toggle-info')"
          >
            info
          </span>
        </template>
        <template #tooltip>Info</template>
      </tooltip>
    </div>
  </div>
</template>

<style scoped lang="scss">
.title_container {
  display: flex;
  align-items: center;
  margin: 1.5em 1.5em 5px 1.5em;
}

// The loader fits beside the header actions without increasing header height.
.share-dialog > :deep(.loading_wrapper) {
  width: 32px;
  height: 32px;
  padding: 0;
  align-items: center;
  background: transparent;
}

.share-dialog > :deep(.loading_wrapper > .lds-spinner) {
  flex-shrink: 0;
  transform: scale(0.4);
}

.right {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.icon {
  font-size: 1.1em;
  border: 1px solid var(--classic-border-subtle, #ebebeb);
  border-radius: 50%;
  padding: 0.58em;
  line-height: 1;
  margin-left: 0.5em;
}

.icon.clickable:hover {
  cursor: pointer;
  background: var(--classic-surface-hover, #f4f4f4);
}

.icon.disabled {
  cursor: not-allowed;
}
</style>
